/**
 * Notification Service — localStorage persistence and CRUD for in-app notifications.
 *
 * NOTE: All polling, push registration, and browser-notification logic has been
 * removed. This module now only manages the notification list state for the UI.
 */

import { v4 as uuidv4 } from 'uuid';

// ── Types ────────────────────────────────────────────────────────────────────

export type NotifKind = 'med' | 'reading' | 'reminder';

export interface AppNotification {
  id: string;
  kind: NotifKind;
  title: string;
  body: string;
  /** Raw interpolation value for re-translation at render time (e.g. med list for 'med' kind). */
  rawBody?: string;
  time: string; // display string, e.g. "8:00 AM"
  timestamp: number; // unix ms for sorting
  read: boolean;
  /** Pet the reminder belongs to (for cross-pet disambiguation). */
  petId?: string;
  petName?: string;
  /** Set when the user taps to acknowledge this reminder. */
  acknowledgedAt?: number;
  acknowledgedBy?: string;
}

export interface NotificationStrings {
  medReminderTitle: string;
  medReminderBody: string;
  dailyReminderTitle: string;
  dailyReminderBody: string;
}

// ── Storage Keys ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'koda_notifications';
const CLEAR_STORAGE_KEY = 'koda_notification_clears';

/** Keep notifications for 5 days. */
export const NOTIF_TTL_MS = 5 * 24 * 60 * 60 * 1000;

// ── Persistence ──────────────────────────────────────────────────────────────

function loadNotificationClears(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(CLEAR_STORAGE_KEY) || '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

function saveNotificationClears(clears: Record<string, number>): void {
  try {
    localStorage.setItem(CLEAR_STORAGE_KEY, JSON.stringify(clears));
  } catch (e) {
    console.error('Error saving notification clears:', e);
  }
}

export function getNotificationClearedAt(petId: string | null | undefined): number {
  if (!petId) return 0;
  const clearedAt = loadNotificationClears()[petId];
  return Number.isFinite(clearedAt) ? clearedAt : 0;
}

export function filterVisibleNotifications(notifications: AppNotification[]): AppNotification[] {
  const clears = loadNotificationClears();
  return notifications.filter(notification => {
    if (!notification.petId) return true;
    const clearedAt = clears[notification.petId];
    if (!Number.isFinite(clearedAt)) return true;
    return notification.timestamp > clearedAt;
  });
}

export function markNotificationsClearedForPet(petId: string): void {
  if (!petId) return;
  const clears = loadNotificationClears();
  clears[petId] = Date.now();
  saveNotificationClears(clears);
}

export function loadNotifications(): AppNotification[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const notifications = JSON.parse(stored) as AppNotification[];
      const cutoff = Date.now() - NOTIF_TTL_MS;
      return filterVisibleNotifications(notifications.filter(n => n.timestamp > cutoff));
    }
  } catch (e) {
    console.error('Error loading notifications:', e);
  }
  return [];
}

export function saveNotifications(notifications: AppNotification[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
    window.dispatchEvent(new CustomEvent('koda-notifications-updated'));
  } catch (e) {
    console.error('Error saving notifications:', e);
  }
}

const NOTIF_DEDUP_WINDOW_MS = 2 * 60 * 1000;

export function addNotification(notification: Omit<AppNotification, 'id' | 'timestamp'> & { id?: string }): AppNotification {
  const now = Date.now();
  const newNotif: AppNotification = {
    ...notification,
    id: notification.id || uuidv4(),
    timestamp: now,
  };

  const current = loadNotifications();

  // Deterministic IDs (for shared reminders) should upsert in-place so all
  // devices/accounts converge on one notification record.
  const sameId = current.find(existing => existing.id === newNotif.id);
  if (sameId) {
    const updated = current
      .map(existing => existing.id === newNotif.id ? { ...existing, ...newNotif, timestamp: existing.timestamp } : existing)
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 50);
    saveNotifications(updated);
    return updated.find(n => n.id === newNotif.id) || newNotif;
  }

  // Deduplicate reminders coming from different sources in the same short window
  const duplicate = current.find((existing) => {
    if (existing.kind !== newNotif.kind) return false;
    if ((now - existing.timestamp) > NOTIF_DEDUP_WINDOW_MS) return false;
    const sameTitle = existing.title.trim().toLowerCase() === newNotif.title.trim().toLowerCase();
    const sameBody = existing.body.trim().toLowerCase() === newNotif.body.trim().toLowerCase();
    return sameTitle && sameBody;
  });

  if (duplicate) {
    return duplicate;
  }

  const updated = [newNotif, ...current].slice(0, 50);
  saveNotifications(updated);
  return newNotif;
}

export function markNotificationRead(id: string): void {
  const notifications = loadNotifications();
  const updated = notifications.map(n => n.id === id ? { ...n, read: true } : n);
  saveNotifications(updated);
}

export function markAllNotificationsRead(): void {
  const notifications = loadNotifications();
  const updated = notifications.map(n => ({ ...n, read: true }));
  saveNotifications(updated);
}

export function dismissNotification(id: string): void {
  const notifications = loadNotifications();
  const updated = notifications.filter(n => n.id !== id);
  saveNotifications(updated);
}

export function clearNotificationsForPet(petId: string): void {
  if (!petId) return;
  const notifications = loadNotifications();
  const updated = notifications.filter(n => n.petId !== petId);
  markNotificationsClearedForPet(petId);
  saveNotifications(updated);
}

export function clearAllNotifications(petId?: string): void {
  if (petId) {
    clearNotificationsForPet(petId);
    return;
  }
  saveNotifications([]);
}
